import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, HomeIcon, KeyIcon, BriefcaseIcon, TrendingUpIcon, StarIcon, CrownIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
interface CareerLevel {
  id: string;
  level: string;
  title: string;
  requirements: string;
  rentals: string;
  sales: string;
  income: string;
  description: string;
  color: string;
  bgColor: string;
  icon: any;
  iconBg: string;
}
const careerLevels: CareerLevel[] = [{
  id: 'trainee',
  level: 'NIVEL 1',
  title: 'Trainee',
  requirements: 'Finalizare onboarding și training inițial',
  rentals: '--',
  sales: '--',
  income: '0€ - În formare',
  description: 'Etapa de formare – parcurgi trainingurile și procesul de onboarding.',
  color: 'text-gray-700',
  bgColor: 'bg-gray-50',
  icon: GraduationCapIcon,
  iconBg: 'bg-gray-400'
}, {
  id: 'rental-closer',
  level: 'NIVEL 2',
  title: 'Rental Closer',
  requirements: '0-1 închirieri finalizate',
  rentals: '0-1',
  sales: '0',
  income: '800€ - 1.500€/lună',
  description: 'Primești acces la listele de închiriere și gestionezi primele tranzacții.',
  color: 'text-blue-700',
  bgColor: 'bg-blue-50',
  icon: HomeIcon,
  iconBg: 'bg-blue-500'
}, {
  id: 'rental-buyer',
  level: 'NIVEL 3',
  title: 'Rental Buyer',
  requirements: '1+ închirieri finalizate',
  rentals: '1+',
  sales: '0',
  income: '1.500€ - 2.500€/lună',
  description: 'După prima tranzacție închiriată, primești leaduri de cumpărători.',
  color: 'text-blue-700',
  bgColor: 'bg-blue-100',
  icon: KeyIcon,
  iconBg: 'bg-blue-600'
}, {
  id: 'selling-closer',
  level: 'NIVEL 4',
  title: 'Selling Closer',
  requirements: '3+ închirieri + 1 vânzare',
  rentals: '3+',
  sales: '1',
  income: '2.500€ - 4.000€/lună',
  description: 'Cu minim 3 închirieri finalizate, se deblochează listele de proprietari. Poți gestiona cumpărători și proprietari pe segmentul de vânzare.',
  color: 'text-green-700',
  bgColor: 'bg-green-50',
  icon: BriefcaseIcon,
  iconBg: 'bg-green-500'
}, {
  id: 'selling-broker',
  level: 'NIVEL 5',
  title: 'Selling Broker',
  requirements: '3+ închirieri + 4 vânzări',
  rentals: '3+',
  sales: '4',
  income: '4.000€ - 6.000€/lună',
  description: 'Începi să lucrezi cu portofolii complexe și primești leaduri premium.',
  color: 'text-green-700',
  bgColor: 'bg-green-100',
  icon: TrendingUpIcon,
  iconBg: 'bg-green-600'
}, {
  id: 'selling-senior',
  level: 'NIVEL 6',
  title: 'Selling Senior',
  requirements: '3+ închirieri + 9 vânzări',
  rentals: '3+',
  sales: '9',
  income: '6.000€ - 9.000€/lună',
  description: 'Nivel avansat – agent stabil, cu portofoliu personal consistent.',
  color: 'text-green-800',
  bgColor: 'bg-green-200',
  icon: StarIcon,
  iconBg: 'bg-green-700'
}, {
  id: 'selling-master',
  level: 'NIVEL 7',
  title: 'Selling Master',
  requirements: '3+ închirieri + 10+ vânzări',
  rentals: '3+',
  sales: '10+',
  income: '9.000€+/lună',
  description: 'Nivel de top – agentul devine mentor și poate coordona alți agenți.',
  color: 'text-yellow-800',
  bgColor: 'bg-yellow-50',
  icon: CrownIcon,
  iconBg: 'bg-yellow-500'
}];
export function CareerJourney() {
  const [expandedLevel, setExpandedLevel] = useState<string | null>(null);
  const toggleLevel = (levelId: string) => {
    setExpandedLevel(expandedLevel === levelId ? null : levelId);
  };
  return <div className="max-w-4xl mx-auto" data-id="element-469">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="text-center mb-12" data-id="element-470">
        <h2 className="text-3xl md:text-4xl font-bold text-[#132C53] mb-4" data-id="element-471">
          Călătoria Ta către Succes
        </h2>
        <p className="text-gray-600 text-lg" data-id="element-472">
          7 nivele clare de carieră. Fiecare pas te aduce mai aproape de
          venituri consistente și libertate financiară.
        </p>
      </motion.div>
      <div className="relative" data-id="element-473">
        <div className="absolute left-[34px] top-0 bottom-0 w-1 bg-gradient-to-b from-gray-300 via-blue-400 via-green-400 to-yellow-400 hidden md:block" data-id="element-474"></div>
        <div className="space-y-4" data-id="element-475">
          {careerLevels.map((level, index) => {
          const Icon = level.icon;
          const isExpanded = expandedLevel === level.id;
          return <motion.div key={level.id} initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="relative" data-id="element-476">
                <div className="flex items-start space-x-4" data-id="element-477">
                  <div className={`flex-shrink-0 w-[70px] h-[70px] ${level.iconBg} rounded-full flex items-center justify-center shadow-lg z-10`} data-id="element-478">
                    <Icon className="w-8 h-8 text-white" data-id="element-479" />
                  </div>
                  <div className="flex-1" data-id="element-480">
                    <button onClick={() => toggleLevel(level.id)} className={`w-full ${level.bgColor} rounded-2xl p-5 shadow-md hover:shadow-lg transition-all border-2 border-gray-200 text-left`} data-id="element-481">
                      <div className="flex items-center justify-between" data-id="element-482">
                        <div className="flex-1" data-id="element-483">
                          <div className="flex items-center space-x-2 mb-2" data-id="element-484">
                            <span className="text-xs font-bold text-gray-500" data-id="element-485">
                              {level.level}
                            </span>
                            <span className={`text-xl font-bold ${level.color}`} data-id="element-486">
                              {level.title}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1" data-id="element-487">
                            {level.requirements}
                          </p>
                          {(level.rentals !== '--' || level.sales !== '--') && <p className="text-xs text-gray-500 italic mb-2" data-id="element-488">
                              (Total de la start activitate)
                            </p>}
                          <div className="flex items-center space-x-2" data-id="element-489">
                            <ArrowRightIcon className="w-4 h-4 text-[#357BEB]" data-id="element-490" />
                            <span className="text-sm font-semibold text-[#357BEB]" data-id="element-491">
                              {level.income}
                            </span>
                          </div>
                        </div>
                        <motion.div animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.3
                    }} data-id="element-492">
                          <ChevronDownIcon className="w-6 h-6 text-gray-400" data-id="element-493" />
                        </motion.div>
                      </div>
                      <AnimatePresence data-id="element-494">
                        {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3
                    }} className="overflow-hidden" data-id="element-495">
                            <div className="mt-4 pt-4 border-t border-gray-300" data-id="element-496">
                              <p className="text-gray-700 mb-3" data-id="element-497">
                                {level.description}
                              </p>
                              <div className="grid grid-cols-2 gap-3" data-id="element-498">
                                <div className="bg-white bg-opacity-50 rounded-lg p-3" data-id="element-499">
                                  <p className="text-xs text-gray-600 mb-1" data-id="element-500">
                                    Închirieri (total)
                                  </p>
                                  <p className="text-lg font-bold text-[#132C53]" data-id="element-501">
                                    {level.rentals}
                                  </p>
                                </div>
                                <div className="bg-white bg-opacity-50 rounded-lg p-3" data-id="element-502">
                                  <p className="text-xs text-gray-600 mb-1" data-id="element-503">
                                    Vânzări (total)
                                  </p>
                                  <p className="text-lg font-bold text-[#132C53]" data-id="element-504">
                                    {level.sales}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </button>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.8
    }} className="mt-12 bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white p-6 rounded-2xl text-center" data-id="element-505">
        <p className="text-lg font-medium" data-id="element-506">
          💡 Fiecare nivel se deblochează automat când îndeplinești cerințele.
          Performanța ta dictează viteza de avansare!
        </p>
      </motion.div>
    </div>;
}